(function () {
  'use strict';

  var activeOffer = null;
  var activeOneTime = false;
  var offerRoot = null;
  var requestInFlight = false;

  function findAll(root, selector) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = Array.prototype.slice.call(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) nodes.unshift(scope);
    return nodes;
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = !!hidden;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initMarquee(root) {
    findAll(root, '[data-biotin-marquee]').forEach(function (marquee) {
      if (marquee.__biotinBound) return;
      marquee.__biotinBound = true;

      var track = marquee.querySelector('.biotin-marquee__track');
      var sets = Array.prototype.slice.call(marquee.querySelectorAll('[data-biotin-marquee-set]'));
      if (!track || sets.length !== 2) return;

      function clearClones() {
        sets.forEach(function (set) {
          set.querySelectorAll('[data-biotin-marquee-clone]').forEach(function (clone) { clone.remove(); });
        });
      }

      function makeClone(item) {
        var clone = item.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('data-biotin-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      }

      function fill() {
        clearClones();
        var originals = Array.prototype.slice.call(sets[0].children);
        if (!originals.length) return;

        var minimumWidth = marquee.clientWidth + 1;
        var rounds = 0;
        while (sets[0].scrollWidth < minimumWidth && rounds < 24) {
          originals.forEach(function (item) {
            sets[0].appendChild(makeClone(item));
            sets[1].appendChild(makeClone(item));
          });
          rounds += 1;
        }

        var distance = sets[0].scrollWidth;
        track.style.setProperty('--biotin-marquee-offset', '-' + distance + 'px');
        track.style.setProperty('--biotin-marquee-duration', Math.max(18, distance / 42) + 's');
      }

      fill();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fill);
      if (window.ResizeObserver) {
        marquee.__biotinResizeObserver = new ResizeObserver(fill);
        marquee.__biotinResizeObserver.observe(marquee);
      } else {
        window.addEventListener('resize', fill, { passive: true });
      }
    });
  }

  function initGallery(root) {
    findAll(root, '[data-biotin-gallery]').forEach(function (gallery) {
      if (gallery.__biotinBound) return;
      gallery.__biotinBound = true;
      var viewport = gallery.querySelector('[data-biotin-gallery-viewport]');
      var track = gallery.querySelector('[data-biotin-gallery-track]');
      var physicalSlides = Array.prototype.slice.call(gallery.querySelectorAll('[data-biotin-gallery-slide]'));
      var slides = physicalSlides.filter(function (slide) { return slide.getAttribute('data-gallery-copy') === 'original'; });
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-biotin-gallery-thumb]'));
      var thumbRail = gallery.querySelector('.biotin-gallery__thumbs');
      var previous = gallery.querySelector('[data-biotin-gallery-previous]');
      var next = gallery.querySelector('[data-biotin-gallery-next]');
      var currentIndex = 0;
      var currentPhysical = 0;
      var originalStart = 0;
      var loopEnabled = false;
      var resizeFrame = null;
      var transitionTimer = null;
      var isAnimating = false;
      var drag = null;
      if (!slides.length) slides = physicalSlides.slice();
      if (!viewport || !track || !slides.length || slides.length !== thumbs.length) return;

      originalStart = Math.max(0, physicalSlides.indexOf(slides[0]));
      currentPhysical = originalStart;
      loopEnabled = slides.length > 1 && physicalSlides.length >= slides.length * 3;

      function normalizeIndex(index) {
        var normalized = index % slides.length;
        return normalized < 0 ? normalized + slides.length : normalized;
      }

      function realIndexForPhysical(index) {
        return normalizeIndex(index - originalStart);
      }

      function thumbRailIsHorizontal() {
        return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
      }

      function setTrackPosition(x, animate) {
        var shouldAnimate = animate !== false && !prefersReducedMotion();

        if (shouldAnimate) track.style.removeProperty('transition');
        else track.style.transition = 'none';
        track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
        if (!shouldAnimate) {
          window.requestAnimationFrame(function () {
            if (!gallery.classList.contains('is-dragging') && !isAnimating) track.style.removeProperty('transition');
          });
        }
        return shouldAnimate;
      }

      function positionForPhysical(index) {
        return -(index * viewport.clientWidth);
      }

      function centerActiveThumb(thumb, animate) {
        if (!thumb || !thumbRail || !thumbRailIsHorizontal() || !thumbRail.scrollTo) return;
        var left = thumb.offsetLeft - ((thumbRail.clientWidth - thumb.offsetWidth) / 2);
        thumbRail.scrollTo({
          left: Math.max(0, left),
          behavior: animate && !prefersReducedMotion() ? 'smooth' : 'auto'
        });
      }

      function syncUi(index, animate) {
        currentIndex = normalizeIndex(index);
        slides.forEach(function (slide, slideIndex) {
          slide.setAttribute('aria-hidden', slideIndex === currentIndex ? 'false' : 'true');
        });
        physicalSlides.forEach(function (slide) {
          if (slide.getAttribute('data-gallery-copy') !== 'original') slide.setAttribute('aria-hidden', 'true');
        });
        thumbs.forEach(function (thumb, thumbIndex) {
          var selected = thumbIndex === currentIndex;
          thumb.classList.toggle('is-active', selected);
          thumb.setAttribute('aria-pressed', selected ? 'true' : 'false');
          thumb.setAttribute('tabindex', selected ? '0' : '-1');
        });

        if (previous) previous.disabled = slides.length < 2;
        if (next) next.disabled = slides.length < 2;
        centerActiveThumb(thumbs[currentIndex], animate);
      }

      function clearTransitionTimer() {
        if (!transitionTimer) return;
        window.clearTimeout(transitionTimer);
        transitionTimer = null;
      }

      function finishTransition() {
        clearTransitionTimer();
        isAnimating = false;
        if (loopEnabled && (currentPhysical < originalStart || currentPhysical >= originalStart + slides.length)) {
          currentPhysical = originalStart + currentIndex;
          setTrackPosition(positionForPhysical(currentPhysical), false);
        }
      }

      function goToPhysical(index, animate) {
        var target = Math.max(0, Math.min(physicalSlides.length - 1, index));
        currentPhysical = target;
        syncUi(realIndexForPhysical(currentPhysical), animate !== false);
        clearTransitionTimer();
        isAnimating = setTrackPosition(positionForPhysical(currentPhysical), animate);
        if (isAnimating) transitionTimer = window.setTimeout(finishTransition, 500);
        else finishTransition();
      }

      function nearestPhysicalForReal(index) {
        var real = normalizeIndex(index);
        var nearest = originalStart + real;
        var nearestDistance = Math.abs(nearest - currentPhysical);
        physicalSlides.forEach(function (_slide, physicalIndex) {
          if (realIndexForPhysical(physicalIndex) !== real) return;
          var distance = Math.abs(physicalIndex - currentPhysical);
          if (distance < nearestDistance) {
            nearest = physicalIndex;
            nearestDistance = distance;
          }
        });
        return nearest;
      }

      function activate(index, animate) {
        if (isAnimating) return;
        goToPhysical(nearestPhysicalForReal(Number(index) || 0), animate);
      }

      function step(direction) {
        if (isAnimating || slides.length < 2) return;
        goToPhysical(currentPhysical + direction, true);
      }

      function activateThumb(thumb, animate) {
        if (!thumb) return;
        var index = thumbs.indexOf(thumb);
        if (index < 0) return;
        activate(index, animate);
      }

      thumbs.forEach(function (thumb, thumbIndex) {
        thumb.addEventListener('click', function () { activate(thumbIndex, true); });
        thumb.addEventListener('keydown', function (event) {
          var previousKey = 'ArrowLeft';
          var nextKey = 'ArrowRight';
          if (event.key !== previousKey && event.key !== nextKey && event.key !== 'Home' && event.key !== 'End') return;
          event.preventDefault();
          var targetIndex = currentIndex;
          if (event.key === previousKey) targetIndex -= 1;
          if (event.key === nextKey) targetIndex += 1;
          if (event.key === 'Home') targetIndex = 0;
          if (event.key === 'End') targetIndex = thumbs.length - 1;
          targetIndex = normalizeIndex(targetIndex);
          activate(targetIndex, true);
          thumbs[targetIndex].focus({ preventScroll: true });
        });
      });

      if (previous) previous.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      track.addEventListener('transitionend', function (event) {
        if (event.target === track && event.propertyName === 'transform') finishTransition();
      });

      viewport.addEventListener('dragstart', function (event) { event.preventDefault(); });
      viewport.addEventListener('pointerdown', function (event) {
        if (slides.length < 2 || isAnimating || (event.pointerType === 'mouse' && event.button !== 0)) return;
        drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          baseX: positionForPhysical(currentPhysical),
          deltaX: 0,
          moved: false,
          vertical: false
        };
      });
      viewport.addEventListener('pointermove', function (event) {
        if (!drag || drag.pointerId !== event.pointerId || drag.vertical) return;
        var deltaX = event.clientX - drag.startX;
        var deltaY = event.clientY - drag.startY;
        if (!drag.moved && Math.abs(deltaX) < 6) return;
        if (!drag.moved && Math.abs(deltaY) > Math.abs(deltaX)) {
          drag.vertical = true;
          return;
        }
        if (!drag.moved) {
          drag.moved = true;
          gallery.classList.add('is-dragging');
          if (viewport.setPointerCapture) viewport.setPointerCapture(event.pointerId);
        }
        drag.deltaX = deltaX;
        event.preventDefault();
        track.style.transition = 'none';
        track.style.transform = 'translate3d(' + (drag.baseX + deltaX) + 'px, 0, 0)';
      });

      function endDrag(event, cancelled) {
        if (!drag || drag.pointerId !== event.pointerId) return;
        var dragState = drag;
        drag = null;
        gallery.classList.remove('is-dragging');
        if (viewport.hasPointerCapture && viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        if (!dragState.moved || cancelled) {
          goToPhysical(currentPhysical, dragState.moved);
          return;
        }

        var width = Math.max(1, viewport.clientWidth);
        var shift = Math.round(-dragState.deltaX / width);
        if (shift === 0 && Math.abs(dragState.deltaX) >= Math.min(48, width * 0.14)) shift = dragState.deltaX < 0 ? 1 : -1;
        shift = Math.max(-2, Math.min(2, shift));
        goToPhysical(currentPhysical + shift, true);
      }

      viewport.addEventListener('pointerup', function (event) { endDrag(event, false); });
      viewport.addEventListener('pointercancel', function (event) { endDrag(event, true); });

      window.addEventListener('resize', function () {
        if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(function () {
          clearTransitionTimer();
          isAnimating = false;
          currentPhysical = originalStart + currentIndex;
          setTrackPosition(positionForPhysical(currentPhysical), false);
          resizeFrame = null;
        });
      }, { passive: true });

      syncUi(0, false);
      goToPhysical(originalStart, false);
      gallery.__biotinActivate = function (thumb) { activateThumb(thumb, true); };
    });
  }

  function initReviewCarousel(carousel) {
    if (carousel.__biotinBound) return;
    carousel.__biotinBound = true;

    var track = carousel.querySelector('[data-biotin-review-track]');
    var physicalSlides = Array.prototype.slice.call(carousel.querySelectorAll('[data-biotin-review-slide]'));
    var slides = physicalSlides.filter(function (slide) { return slide.getAttribute('data-review-copy') === 'original'; });
    var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-biotin-review-dot]'));
    var currentIndex = 0;
    var originalStart = 0;
    var loopEnabled = false;
    var scrollFrame = null;
    var resizeFrame = null;
    var settleTimer = null;
    var drag = null;
    if (!slides.length) slides = physicalSlides.slice();
    if (!track || !slides.length || slides.length !== dots.length) return;

    originalStart = Math.max(0, physicalSlides.indexOf(slides[0]));
    loopEnabled = slides.length > 1 && physicalSlides.length >= slides.length * 3;

    function isMobile() {
      return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    }

    function normalizeIndex(index) {
      var normalized = index % slides.length;
      return normalized < 0 ? normalized + slides.length : normalized;
    }

    function realIndexForPhysical(index) {
      return normalizeIndex(index - originalStart);
    }

    function slideLeft(index) {
      return physicalSlides[index].offsetLeft - physicalSlides[0].offsetLeft;
    }

    function updateDots(index) {
      currentIndex = normalizeIndex(index);
      dots.forEach(function (dot, dotIndex) {
        var active = dotIndex === currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    function closestPhysicalIndex() {
      var closestIndex = 0;
      var closestDistance = Infinity;
      physicalSlides.forEach(function (_slide, index) {
        var distance = Math.abs(slideLeft(index) - track.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    }

    function scrollToPhysical(index, animate) {
      var target = Math.max(0, Math.min(physicalSlides.length - 1, index));
      var left = slideLeft(target);
      if (track.scrollTo) {
        track.scrollTo({
          left: left,
          behavior: animate && !prefersReducedMotion() ? 'smooth' : 'auto'
        });
      } else {
        track.scrollLeft = left;
      }
    }

    function nearestPhysicalForReal(index) {
      var real = normalizeIndex(index);
      var currentPhysical = closestPhysicalIndex();
      var nearest = originalStart + real;
      var nearestDistance = Math.abs(nearest - currentPhysical);
      physicalSlides.forEach(function (_slide, physicalIndex) {
        if (realIndexForPhysical(physicalIndex) !== real) return;
        var distance = Math.abs(physicalIndex - currentPhysical);
        if (distance < nearestDistance) {
          nearest = physicalIndex;
          nearestDistance = distance;
        }
      });
      return nearest;
    }

    function clearSettleTimer() {
      if (!settleTimer) return;
      window.clearTimeout(settleTimer);
      settleTimer = null;
    }

    function rebaseIfNeeded() {
      clearSettleTimer();
      if (!isMobile()) return;
      if (drag && drag.moved) {
        settleTimer = window.setTimeout(rebaseIfNeeded, 140);
        return;
      }
      var physicalIndex = closestPhysicalIndex();
      var realIndex = realIndexForPhysical(physicalIndex);
      updateDots(realIndex);
      if (loopEnabled && (physicalIndex < originalStart || physicalIndex >= originalStart + slides.length)) {
        scrollToPhysical(originalStart + realIndex, false);
      }
    }

    function scheduleRebase(delay) {
      clearSettleTimer();
      settleTimer = window.setTimeout(rebaseIfNeeded, delay || 140);
    }

    function activate(index, animate) {
      var nextIndex = normalizeIndex(Number(index) || 0);
      updateDots(nextIndex);
      if (!isMobile()) return;
      scrollToPhysical(nearestPhysicalForReal(nextIndex), animate);
      scheduleRebase(animate ? 500 : 0);
    }

    dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () { activate(dotIndex, true); });
      dot.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        var targetIndex = currentIndex;
        if (event.key === 'ArrowLeft') targetIndex -= 1;
        if (event.key === 'ArrowRight') targetIndex += 1;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = dots.length - 1;
        targetIndex = normalizeIndex(targetIndex);
        activate(targetIndex, true);
        dots[targetIndex].focus({ preventScroll: true });
      });
    });

    track.addEventListener('scroll', function () {
      if (!isMobile() || scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(function () {
        updateDots(realIndexForPhysical(closestPhysicalIndex()));
        scrollFrame = null;
      });
      scheduleRebase(140);
    }, { passive: true });

    track.addEventListener('scrollend', rebaseIfNeeded);
    track.addEventListener('dragstart', function (event) { event.preventDefault(); });
    track.addEventListener('pointerdown', function (event) {
      if (!isMobile() || slides.length < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScroll: track.scrollLeft,
        startPhysical: closestPhysicalIndex(),
        deltaX: 0,
        moved: false,
        vertical: false
      };
    });
    track.addEventListener('pointermove', function (event) {
      if (!drag || drag.pointerId !== event.pointerId || drag.vertical) return;
      var deltaX = event.clientX - drag.startX;
      var deltaY = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(deltaX) < 6) return;
      if (!drag.moved && Math.abs(deltaY) > Math.abs(deltaX)) {
        drag.vertical = true;
        return;
      }
      if (!drag.moved) {
        drag.moved = true;
        track.classList.add('is-dragging');
        if (track.setPointerCapture) track.setPointerCapture(event.pointerId);
      }
      drag.deltaX = deltaX;
      event.preventDefault();
      track.scrollLeft = drag.startScroll - deltaX;
    });

    function endDrag(event, cancelled) {
      if (!drag || drag.pointerId !== event.pointerId) return;
      var dragState = drag;
      drag = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture && track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      if (!dragState.moved || cancelled) {
        scrollToPhysical(dragState.startPhysical, dragState.moved);
        scheduleRebase(dragState.moved ? 500 : 0);
        return;
      }

      var width = Math.max(1, track.clientWidth);
      var shift = Math.round(-dragState.deltaX / width);
      if (shift === 0 && Math.abs(dragState.deltaX) >= Math.min(48, width * 0.14)) shift = dragState.deltaX < 0 ? 1 : -1;
      shift = Math.max(-2, Math.min(2, shift));
      scrollToPhysical(dragState.startPhysical + shift, true);
      scheduleRebase(500);
    }

    track.addEventListener('pointerup', function (event) { endDrag(event, false); });
    track.addEventListener('pointercancel', function (event) { endDrag(event, true); });

    window.addEventListener('resize', function () {
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(function () {
        clearSettleTimer();
        if (isMobile()) scrollToPhysical(originalStart + currentIndex, false);
        else track.scrollLeft = 0;
        resizeFrame = null;
      });
    }, { passive: true });

    updateDots(0);
    window.requestAnimationFrame(function () {
      if (isMobile()) scrollToPhysical(originalStart, false);
    });
    carousel.__biotinActivateReview = function (slide) {
      var index = slides.indexOf(slide);
      if (index >= 0) activate(index, true);
    };
  }

  function initReviewCarousels(root) {
    findAll(root, '[data-biotin-review-carousel]').forEach(initReviewCarousel);
  }

  function showOfferError(message) {
    if (!offerRoot) return;
    var error = offerRoot.querySelector('[data-biotin-offer-error]');
    if (!error) return;
    error.textContent = message || '';
    setHidden(error, !message);
  }

  function applyOfferDisplay(offer, oneTime) {
    if (!offer) return;
    var frequency = oneTime ? offer.getAttribute('data-one-time-frequency-label') : offer.getAttribute('data-frequency-label');
    var unitPrice = oneTime ? offer.getAttribute('data-one-time-unit-price-label') : offer.getAttribute('data-unit-price-label');
    var totalPrice = oneTime ? offer.getAttribute('data-one-time-total-label') : offer.getAttribute('data-total-label');
    var frequencyNode = offer.querySelector('[data-biotin-offer-frequency]');
    var unitPriceNode = offer.querySelector('[data-biotin-offer-unit-price]');
    var totalPriceNode = offer.querySelector('[data-biotin-offer-total-price]');
    if (frequencyNode) frequencyNode.textContent = frequency || '';
    if (unitPriceNode) unitPriceNode.textContent = unitPrice || '';
    if (totalPriceNode) totalPriceNode.textContent = totalPrice || '';
  }

  function updatePurchaseControls(offer, oneTime) {
    if (!offer) return;
    var offerLabel = offer.getAttribute('data-offer-label') || '';
    var totalLabel = oneTime ? offer.getAttribute('data-one-time-total-label') || '' : offer.getAttribute('data-total-label') || '';
    var ctaLabel = offer.getAttribute('data-cta-label') || 'Get Started';
    var combinedLabel = totalLabel ? ctaLabel + ' — ' + totalLabel : ctaLabel;

    if (offerRoot) {
      offerRoot.querySelectorAll('[data-biotin-offer]').forEach(function (item) { applyOfferDisplay(item, false); });
    }
    applyOfferDisplay(offer, oneTime);

    document.querySelectorAll('[data-biotin-submit]').forEach(function (button) {
      if (!button.hasAttribute('data-biotin-static-label')) button.textContent = combinedLabel;
      button.disabled = false;
    });
    document.querySelectorAll('[data-biotin-sticky-offer]').forEach(function (node) { node.textContent = offerLabel; });
    document.querySelectorAll('[data-biotin-sticky-price]').forEach(function (node) { node.textContent = totalLabel; });
    offerRoot.querySelectorAll('[data-biotin-select-one-time]').forEach(function (button) {
      button.setAttribute('aria-pressed', oneTime ? 'true' : 'false');
    });
    document.dispatchEvent(new CustomEvent('biotin:offer-change', {
      detail: { offer: offer, label: offerLabel, total: totalLabel, oneTime: !!oneTime }
    }));
  }

  function selectOffer(offer, announceError, oneTime) {
    if (!offer || !offerRoot) return false;
    if (offer.getAttribute('data-selectable') !== 'true') {
      if (announceError !== false) showOfferError(offer.getAttribute('data-error') || offerRoot.getAttribute('data-cart-error'));
      return false;
    }

    offerRoot.querySelectorAll('[data-biotin-offer]').forEach(function (item) {
      var selected = item === offer;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', selected ? 'true' : 'false');
      item.setAttribute('tabindex', item.getAttribute('data-selectable') === 'true' ? '0' : '-1');
    });
    activeOffer = offer;
    activeOneTime = oneTime === true;
    showOfferError('');
    updatePurchaseControls(offer, activeOneTime);
    return true;
  }

  function initOffers(root) {
    findAll(root, '[data-biotin-offers]').forEach(function (container) {
      if (container.__biotinBound) return;
      container.__biotinBound = true;
      offerRoot = container;

      var offers = Array.prototype.slice.call(container.querySelectorAll('[data-biotin-offer]'));
      offers.forEach(function (offer) {
        offer.addEventListener('click', function () { selectOffer(offer, true); });
        offer.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowUp' && event.key !== 'ArrowRight' && event.key !== 'ArrowDown') return;
          var selectableOffers = offers.filter(function (item) { return item.getAttribute('data-selectable') === 'true'; });
          if (!selectableOffers.length) return;
          event.preventDefault();
          var current = Math.max(0, selectableOffers.indexOf(offer));
          var direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
          var target = selectableOffers[(current + direction + selectableOffers.length) % selectableOffers.length];
          if (selectOffer(target, false)) target.focus({ preventScroll: true });
        });
      });

      var requested = container.querySelector('[data-biotin-offer].is-requested-default[data-selectable="true"]');
      var firstSelectable = container.querySelector('[data-biotin-offer][data-selectable="true"]');
      if (!selectOffer(requested || firstSelectable, false) && offers.length) offers[0].setAttribute('tabindex', '0');

      var oneTimeButton = container.querySelector('[data-biotin-select-one-time]');
      if (oneTimeButton) {
        oneTimeButton.addEventListener('click', function () {
          var oneTimeOffer = container.querySelector('[data-biotin-offer][data-one-time-eligible="true"][data-variant-available="true"]');
          if (oneTimeOffer && selectOffer(oneTimeOffer, true, true)) {
            oneTimeOffer.focus({ preventScroll: true });
            oneTimeOffer.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
          } else {
            showOfferError(container.getAttribute('data-one-time-error'));
          }
        });
      }
    });
  }

  function parseResponse(response) {
    return response.json().catch(function () { return {}; }).then(function (body) {
      if (!response.ok) {
        var error = new Error(body.description || body.message || 'Cart request failed');
        error.payload = body;
        throw error;
      }
      return body;
    });
  }

  var LEGACY_BIOTIN_BUNDLE_VARIANT_IDS = [54225002463542, 54225002496310, 54225002529078];

  function removeExistingBiotinLines(rootPath, targetVariantId) {
    return fetch(rootPath + 'cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(parseResponse)
      .then(function (cart) {
        var updates = {};
        (cart.items || []).forEach(function (line) {
          var lineVariantId = Number(line.variant_id || line.id);
          if (lineVariantId === Number(targetVariantId) || LEGACY_BIOTIN_BUNDLE_VARIANT_IDS.indexOf(lineVariantId) !== -1) {
            updates[line.key] = 0;
          }
        });

        if (!Object.keys(updates).length) return null;

        return fetch(rootPath + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates: updates })
        }).then(parseResponse);
      });
  }

  function submitActiveOffer() {
    if (!offerRoot || !activeOffer) {
      if (offerRoot) showOfferError(offerRoot.getAttribute('data-selection-error'));
      return;
    }
    if (requestInFlight) return;

    if (!activeOneTime && activeOffer.getAttribute('data-available') !== 'true') {
      showOfferError(activeOffer.getAttribute('data-error') || offerRoot.getAttribute('data-cart-error'));
      return;
    }
    if (activeOneTime && activeOffer.getAttribute('data-variant-available') !== 'true') {
      showOfferError(offerRoot.getAttribute('data-one-time-error'));
      return;
    }

    var variantId = activeOffer.getAttribute('data-variant-id');
    var quantity = parseInt(activeOffer.getAttribute('data-quantity') || '1', 10);
    var sellingPlan = activeOffer.getAttribute('data-selling-plan');
    if (!variantId || !isFinite(quantity) || quantity < 1) {
      showOfferError(offerRoot.getAttribute('data-selection-error'));
      return;
    }

    var item = { id: Number(variantId), quantity: quantity };
    if (!activeOneTime && sellingPlan) item.selling_plan = Number(sellingPlan);
    requestInFlight = true;
    document.querySelectorAll('[data-biotin-submit]').forEach(function (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    showOfferError('');

    var rootPath = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    removeExistingBiotinLines(rootPath, item.id)
      .then(function () {
        return fetch(rootPath + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [item] })
        }).then(parseResponse);
      })
      .then(function () {
        (window.NutraFunnelCheckout || {
          redirectAfterCartAdd: function (root) {
            window.location.assign((root || '/') + 'checkout');
          }
        }).redirectAfterCartAdd(rootPath);
      })
      .catch(function (error) {
        requestInFlight = false;
        document.querySelectorAll('[data-biotin-submit]').forEach(function (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
        var fallback = offerRoot.getAttribute('data-cart-error') || 'We could not add this offer. Please try again.';
        showOfferError(error && error.message ? error.message : fallback);
      });
  }

  function bindSubmitButtons(root) {
    findAll(root, '[data-biotin-submit]').forEach(function (button) {
      if (button.__biotinBound) return;
      button.__biotinBound = true;
      button.addEventListener('click', submitActiveOffer);
    });
  }

  function initSticky() {
    var sticky = document.querySelector('[data-biotin-sticky]');
    var anchor = document.querySelector('[data-biotin-sticky-anchor]');
    var offers = document.querySelector('[data-biotin-offers]');
    if (!sticky || !anchor || sticky.__biotinBound) return;
    sticky.__biotinBound = true;
    var frame = null;

    function sync() {
      var anchorRect = anchor.getBoundingClientRect();
      var offersRect = offers ? offers.getBoundingClientRect() : null;
      var offersVisible = offersRect && offersRect.top < window.innerHeight * 0.8 && offersRect.bottom > 100;
      var show = anchorRect.bottom < 0 && !offersVisible;
      setHidden(sticky, !show);
      sticky.classList.toggle('is-visible', show);
    }

    function requestSync() {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        sync();
        frame = null;
      });
    }

    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync, { passive: true });
    sync();
  }

  function init(scope) {
    initMarquee(scope);
    initGallery(scope);
    initReviewCarousels(scope);
    initOffers(scope);
    bindSubmitButtons(scope);
    initSticky();
  }

  function handleBlockSelection(event) {
    var target = event.target;
    if (!target) return;

    var faq = target.closest && target.closest('.biotin-faq__item');
    if (faq) faq.open = true;

    var thumb = target.closest && target.closest('[data-biotin-gallery-thumb]');
    if (thumb) {
      var gallery = thumb.closest('[data-biotin-gallery]');
      if (gallery && gallery.__biotinActivate) gallery.__biotinActivate(thumb);
    }

    var reviewSlide = target.closest && target.closest('[data-biotin-review-slide]');
    if (reviewSlide) {
      var reviewCarousel = reviewSlide.closest('[data-biotin-review-carousel]');
      if (reviewCarousel && reviewCarousel.__biotinActivateReview) reviewCarousel.__biotinActivateReview(reviewSlide);
    }

    var offer = target.closest && target.closest('[data-biotin-offer]');
    if (offer) selectOffer(offer, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:block:select', handleBlockSelection);
})();
